// Shared labels and formatters for the provider outreach pages. Vocabulary mirrors
// backend/functions/outreach/constants.js; keep the two in step.

export const OUTREACH_STATUSES = ['not_contacted', 'attempted', 'contacted', 'follow_up', 'engaged', 'referring', 'declined', 'do_not_contact'];
export const STATUS_LABELS = {
  not_contacted: 'Not contacted',
  attempted: 'Attempted',
  contacted: 'Contacted',
  follow_up: 'Follow-up',
  engaged: 'Engaged',
  referring: 'Referring',
  declined: 'Declined',
  do_not_contact: 'Do not contact',
};
// Maps outreach statuses onto the tones the shared StatusPill already knows.
export const STATUS_PILL = {
  not_contacted: 'open',
  attempted: 'new',
  contacted: 'contacted',
  follow_up: 'incomplete',
  engaged: 'running',
  referring: 'converted',
  declined: 'lost',
  do_not_contact: 'failed',
};

export const PRIORITIES = ['P1', 'P2', 'P3', 'REVIEW'];
export const PRIORITY_LABELS = { P1: 'P1 High', P2: 'P2 Medium', P3: 'P3 Low', REVIEW: 'Needs review' };
export const PRIORITY_PILL = { P1: 'failed', P2: 'incomplete', P3: 'neutral', REVIEW: 'paused' };

export const PRACTICE_TYPES = ['Family Medicine', 'Internal Medicine', 'Geriatric Medicine', 'General Practice', 'FQHC', 'Community Health Center', 'Primary Care Clinic', 'Multi-specialty', 'Other'];

export const ACTIVITY_TYPES = [['call', 'Phone call'], ['visit', 'In-person visit'], ['email', 'Email'], ['fax', 'Fax'], ['meeting', 'Meeting'], ['note', 'Note only']];
export const ACTIVITY_LABELS = {
  call: 'Call', visit: 'Visit', email: 'Email', fax: 'Fax', note: 'Note', meeting: 'Meeting', created: 'Created', staff_update: 'Staff update', imported: 'Imported from workbook',
  discovered: 'Discovered by agent', updated: 'Updated by agent', reanalyzed: 'Reanalyzed', closed_signal: 'Closure signal', flagged_duplicate: 'Possible duplicate',
};

export const ORIGIN_LABELS = { agent: 'Research agent', manual: 'Added by staff', import: 'Workbook import' };
export const CHANGE_LABELS = {
  NEW: 'New',
  UNCHANGED: 'Unchanged',
  MATERIALLY_CHANGED: 'Materially changed',
  MINOR_NONMATERIAL_CHANGE: 'Minor change',
  POTENTIAL_DUPLICATE: 'Possible duplicate',
  CLOSED_OR_INACTIVE: 'Closed or inactive',
  REQUIRES_VERIFICATION: 'Needs verification',
};
export const MEDICAID_LABELS = { confirmed: 'Confirmed', likely: 'Likely', unknown: 'Unknown', unlikely: 'Unlikely' };
export const MEDICAID_PILL = { confirmed: 'converted', likely: 'running', unknown: 'neutral', unlikely: 'lost' };
export const VERIFICATION_LABELS = { verified: 'Verified', partially_verified: 'Partially verified', unverified: 'Unverified', manual_unverified: 'Staff entry, unverified' };

export const RUN_MODES = [
  ['incremental', 'Incremental research', 'Collects public sources, compares with saved research and sends only new or materially changed offices to the AI model.'],
  ['refresh_verification', 'Refresh source verification', 'Re-checks every office against the public sources without calling the AI model unless something changed.'],
  ['full_reanalysis', 'Full AI reanalysis', 'Admin only. Re-sends every office to the model and bypasses the cache. This costs money and needs confirmation.'],
];
export const MODE_LABELS = Object.fromEntries(RUN_MODES.map(([k, l]) => [k, l]));
export const RUN_STATUS_LABELS = { queued: 'Queued', running: 'Running', completed: 'Completed', completed_with_errors: 'Completed with errors', failed: 'Failed', stopped: 'Stopped' };
export const ACTIVE_RUN_STATUSES = ['queued', 'running'];
// Plain-language names for the worker stages reported on run.progress.
export const STAGE_LABELS = {
  queued: 'Waiting to start', loadSettings: 'Loading settings', collectSources: 'Collecting public sources', normalizeDedupe: 'Cleaning up candidates',
  compareSaved: 'Comparing with saved offices', analyze: 'Analyzing offices', validateStore: 'Saving results', areaIntelligence: 'Writing area intelligence', finalize: 'Finishing up',
};

// Progress for an active run: a percent plus a sentence like "Analyzing offices: 23 of 146".
export function runProgress(run) {
  if (!run) return { percent: 0, label: STAGE_LABELS.queued };
  const p = run.progress || {};
  const stage = p.stage || run.currentStage || 'queued';
  const label = STAGE_LABELS[stage] || stage;
  const percent = run.status === 'queued' ? 0 : Math.max(0, Math.min(100, Number(p.percent) || 0));
  const detail = p.total ? `: ${fmtNumber(p.done || 0)} of ${fmtNumber(p.total)}` : '';
  return { percent, label: `${label}${detail}` };
}

export const PAGE_SIZES = [10, 15, 25, 50];

export function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { timeZone: 'America/Detroit', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function fmtDateOnly(ymd) {
  if (!ymd) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return fmtDateTime(ymd);
  return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
}

export function fmtDuration(ms) {
  if (ms == null) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function fmtNumber(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  return Number(n).toLocaleString('en-US');
}

export function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

export function fmtCost(n) {
  if (n == null || Number.isNaN(Number(n))) return 'Not priced';
  return `$${Number(n).toFixed(2)}`;
}

export function todayYmd() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Detroit' });
}

// Network failures surface as "Failed to fetch"; staff need a plainer sentence.
export const loadError = (e, fallback) => (!e?.message || /failed to fetch/i.test(e.message) ? fallback : e.message);

export const fullAddress = (o) => [o.street, o.suite, [o.city, o.state].filter(Boolean).join(', '), o.zip].filter(Boolean).join(', ');

export const dueTone = (ymd) => {
  if (!ymd) return '';
  const t = todayYmd();
  if (ymd < t) return 'neg';
  return ymd === t ? 'is-today' : '';
};
